import type { ColoredMaterialResult } from "@color-finder/color-finder-api/models";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Alert, Box, Button, Typography } from "@mui/material";
import axios from "axios";
import { prefetchReferenceSearch } from "hooks/colored-materials";
import type { SortEntry } from "hooks/sort";
import { useDataset } from "providers/DatasetProvider";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { extractColorPalettes } from "../colorMatchApi";
import { useExploreImage } from "../ExploreImageContext";
import { PREFETCH_SEARCH_BEFORE_RESULTS } from "../flowMode";
import type { ExtractResponse, Region } from "../types";
import { RegionAnalysisCard } from "./RegionAnalysisCard";
import { useAnalysisProgress } from "./useAnalysisProgress";

// Must mirror the defaults the results view first renders with
// (ResultsView: sort `deltaE asc`, page 1 → page-1=0, itemPerPage 50, region 0,
// selectedColorIndex 0) so the prefetched params key matches and the seed
// applies on first paint.
const INITIAL_SORT: SortEntry[] = [{ property: "deltaE", direction: "asc" }];
const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 50;

function useRegionThumbnails(regions: Region[], imageSrc: string | null) {
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!imageSrc || regions.length === 0) return;

    const img = new Image();
    img.onload = () => {
      const result: Record<string, string> = {};
      for (const region of regions) {
        const canvas = document.createElement("canvas");
        canvas.width = 240;
        canvas.height = 100;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;

        const sx = (region.x / 100) * img.naturalWidth;
        const sy = (region.y / 100) * img.naturalHeight;
        const sw = (region.width / 100) * img.naturalWidth;
        const sh = (region.height / 100) * img.naturalHeight;

        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 240, 100);
        result[region.id] = canvas.toDataURL("image/jpeg", 0.8);
      }
      setThumbnails(result);
    };
    img.src = imageSrc;
  }, [imageSrc, regions]);

  return thumbnails;
}

const ERROR_MESSAGE_KEYS = {
  400: "ldp_explore_image_error_400",
  401: "ldp_explore_image_error_401",
  403: "ldp_explore_image_error_403",
  413: "ldp_explore_image_error_413",
  network: "ldp_explore_image_error_network",
  default: "ldp_explore_image_error_500",
} as const;

function getErrorMessage(err: unknown, t: (key: string) => string): string {
  if (axios.isAxiosError(err)) {
    if (!err.response) return t(ERROR_MESSAGE_KEYS.network);
    const status = err.response.status;
    const key =
      ERROR_MESSAGE_KEYS[status as keyof typeof ERROR_MESSAGE_KEYS] ??
      (status >= 500 ? ERROR_MESSAGE_KEYS.default : undefined);
    return t(key ?? ERROR_MESSAGE_KEYS.default);
  }
  return t(ERROR_MESSAGE_KEYS.default);
}

export const AnalysisLoadingView = () => {
  const { t } = useTranslation();
  const { state, setExtractResults, setError, goBack } = useExploreImage();
  const { regions, uploadedImage, uploadedFile, imageNaturalSize } = state;
  const isActive = state.flowStep === "analysis";
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const externalViewVisible = settings?.externalViewVisible;
  const { dataset } = useDataset();

  const [apiDone, setApiDone] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const pendingResultsRef = useRef<ExtractResponse | null>(null);
  const pendingSearchRef = useRef<{
    result: ColoredMaterialResult;
    key: string;
  } | null>(null);

  const { progress, isComplete } = useAnalysisProgress(
    regions,
    isActive,
    apiDone,
  );
  const thumbnails = useRegionThumbnails(regions, uploadedImage);

  const regionsRef = useRef(regions);
  regionsRef.current = regions;

  useEffect(() => {
    if (!isActive || !uploadedFile || !imageNaturalSize) return;

    let cancelled = false;

    extractColorPalettes(uploadedFile, regionsRef.current, imageNaturalSize)
      .then(async (results) => {
        if (cancelled) return;
        pendingResultsRef.current = results;

        // Prefetch the first material search for the default region/color so
        // the results view paints without a second loading pass. Loading only
        // ends once this also resolves (see Dev Note: don't end early).
        // Gated by PREFETCH_SEARCH_BEFORE_RESULTS: when off, we skip the
        // prefetch and the results view fetches the search itself (legacy).
        const firstColor = results.regions[0]?.palette?.[0];
        if (PREFETCH_SEARCH_BEFORE_RESULTS && firstColor) {
          // The extracted image color is the reference (`cie_lab`); the results
          // view first renders with no secondary filters, so prefetch with an
          // empty filter list to keep the params key in sync with the seed.
          const { result, key } = await prefetchReferenceSearch(
            firstColor.cieLab,
            [],
            INITIAL_SORT,
            INITIAL_PAGE,
            INITIAL_PAGE_SIZE,
            externalViewVisible,
            { dataset },
          );
          if (cancelled) return;
          pendingSearchRef.current = { result, key };
        }

        setApiDone(true);
      })
      .catch((err) => {
        if (!cancelled) {
          // Discard any extract result already staged (line above runs before
          // the prefetch await) so the isComplete effect does not advance to
          // results when extract succeeded but the first search failed.
          pendingResultsRef.current = null;
          pendingSearchRef.current = null;
          setApiDone(true);
          const message = getErrorMessage(err, t);
          setApiError(message);
          setError(message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    isActive,
    uploadedFile,
    imageNaturalSize,
    externalViewVisible,
    dataset,
    setError,
    t,
  ]);

  useEffect(() => {
    if (isComplete && pendingResultsRef.current) {
      setExtractResults(
        pendingResultsRef.current,
        pendingSearchRef.current?.result ?? null,
        pendingSearchRef.current?.key ?? null,
      );
      pendingResultsRef.current = null;
      pendingSearchRef.current = null;
    }
  }, [isComplete, setExtractResults]);

  useEffect(() => {
    if (!isActive) {
      setApiDone(false);
      setApiError(null);
      pendingResultsRef.current = null;
      pendingSearchRef.current = null;
    }
  }, [isActive]);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 150px)",
        backgroundColor: "var(--alias-background-white, #fff)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--spacing32, 32px)",
        padding: "var(--spacing32, 32px) var(--spacing24, 24px)",
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: "600px" }}>
        <AutoAwesomeIcon
          sx={{
            fontSize: 40,
            color: "var(--alias-text-on-white-brand, #009FE4)",
            marginBottom: "var(--spacing16, 16px)",
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: "var(--heading-h2-28-bold-size, 28px)",
            color: "var(--alias-text-on-white-primary, #1a1a1a)",
            marginBottom: "var(--spacing8, 8px)",
          }}
        >
          {t("ldp_explore_image_analysis_title", {
            count: regions.length,
          })}
        </Typography>
        <Typography
          sx={{
            fontSize: "var(--body-b1-16-regular-size, 16px)",
            color: "var(--alias-text-on-white-secondary, #262626)",
          }}
        >
          {t("ldp_explore_image_analysis_subtitle", {
            count: regions.length,
          })}
        </Typography>
      </Box>

      {apiError && (
        <Alert
          severity="error"
          sx={{ maxWidth: "600px", width: "100%" }}
          action={
            <Button color="inherit" size="small" onClick={() => goBack()}>
              {t("ldp_explore_image_region_back")}
            </Button>
          }
        >
          {apiError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: "var(--spacing24, 24px)",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {regions.map((region) => {
          const regionProgress = progress.find((p) => p.regionId === region.id);
          if (!regionProgress) return null;
          return (
            <RegionAnalysisCard
              key={region.id}
              region={region}
              progress={regionProgress}
              thumbnail={thumbnails[region.id] ?? null}
            />
          );
        })}
      </Box>

      <Typography
        sx={{
          fontSize: "var(--body-b2-14-regular-size, 14px)",
          color: "var(--alias-text-on-white-disable, #999)",
          marginTop: "var(--spacing16, 16px)",
        }}
      >
        {t("ldp_explore_image_analysis_footer")}
      </Typography>
    </Box>
  );
};
